import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ContextService } from './context.service';
import {
  Observable,
  catchError,
  forkJoin,
  map,
  of,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';
import { Octopus } from '@maximizer/core/shared/domain';

@Injectable()
export class TokenService {
  private tokenRequest?: Observable<string>;
  private sessionExpiredRequest?: Observable<boolean>;
  private readonly tokenEndpoint =
    '/Services/AuthenticationService.asmx/GetWebAPIToken';
  private readonly tokenWithIdentityEndpoint =
    '/Services/AuthenticationService.asmx/GetWebAPITokenWithIdentity';
  private readonly sessionExpiredEndpoint =
    '/mvc/SessionExpired/SessionExpired/SessionExpired';
  constructor(
    private readonly http: HttpClient,
    private readonly context: ContextService,
  ) {}

  authenticate(
    database: string,
    user: string,
    password: string,
    vendorId?: string,
    applicationKey?: string,
  ): Observable<string> {
    const request: Octopus.AuthenticationRequest = {
      Database: database,
      UID: user,
      Password: password,
      VendorId: vendorId,
      AppKey: applicationKey,
    };

    return this.http
      .post<Octopus.AuthenticationResponse>(
        `${this.context.api}${Octopus.Action.AUTHENTICATE}`,
        request,
      )
      .pipe(
        map((response) => {
          return response.Data.Token;
        }),
        tap((token) => {
          this.context.token = token;
        }),
      );
  }

  getToken(): Observable<string> {
    if (!this.context.token) {
      this.tokenRequest ??= this.getTokenRequest().pipe(
        map((response) => {
          return response.d;
        }),
        tap((response) => {
          this.context.token = response;
          this.tokenRequest = undefined;
        }),
        shareReplay(1),
      );
      return this.tokenRequest;
    }
    return of(this.context.token);
  }

  isValidToken(): Observable<boolean> {
    return this.http
      .post<Octopus.Response>(
        `${this.context.api}${Octopus.Action.TOKEN_VALID}`,
        {},
      )
      .pipe(
        map(
          (response) =>
            response?.Code === Octopus.ResponseStatusCode.Successful,
        ),
      );
  }

  // For calls outside Octopus, which don't renew the token themselves.
  ensureValidToken(): Observable<string> {
    return this.isValidToken().pipe(
      // A server without the check keeps the current token.
      catchError(() => of(true)),
      switchMap((valid) => {
        if (valid) {
          return of(this.context.token);
        }
        this.context.token = '';
        return this.getToken();
      }),
    );
  }

  getSessionExpired(): Observable<boolean> {
    this.sessionExpiredRequest ??= this.getSessionExpiredRequest().pipe(
      tap(() => {
        this.sessionExpiredRequest = undefined;
      }),
      shareReplay(1),
    );
    return this.sessionExpiredRequest;
  }

  getSession(): Observable<{ token: string; isSessionExpired: boolean }> {
    if (!this.context.token) {
      return forkJoin({
        token: this.getToken(),
        isSessionExpired: this.getSessionExpired(),
      }).pipe(
        map((response) => {
          return {
            token: response.token,
            isSessionExpired: response.isSessionExpired,
          };
        }),
      );
    }
    return of({ token: this.context.token, isSessionExpired: false });
  }

  private getSessionExpiredRequest(): Observable<boolean> {
    return this.http.post<boolean>(
      this.context.website + this.sessionExpiredEndpoint,
      {},
      { observe: 'body' },
    );
  }

  private getTokenRequest(): Observable<{ d: string }> {
    return this.http.post<{ d: string }>(
      this.context.website +
        (this.context.identityToken
          ? this.tokenWithIdentityEndpoint
          : this.tokenEndpoint),
      { IdentityToken: this.context.identityToken },
    );
  }
}
